import { Anchor, Badge, Box, Image, Text, Title } from "@mantine/core"
import { FileTextIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import detail from "../../pages/sales-invoice/details.module.css"
import shared from "../../pages/sales-invoice/sales-invoice-details.module.css"
import { dateFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import classes from "./BuyerInvoiceDetails.module.css"

const IMAGE_URL = /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?|$)/i

// The supplier writes a required description on every attachment; for a buyer
// deciding whether to pay, that note is the useful part, so each file is a row
// with its description and who attached it, not a bare chip.
function AttachmentRow({ attachment }: { attachment: any }) {
  const { t } = useTranslation("common")
  const url: string = attachment?.url ?? ""
  const label = attachment?.name || url.split("/").pop() || t("noData")
  const isImage = IMAGE_URL.test(url)
  const meta = [attachment?.attachedUser ? renderName(attachment.attachedUser) : null, attachment?.createdAt ? dateFormat(attachment.createdAt) : null]
    .filter(Boolean)
    .join(" · ")

  return (
    <Box className={classes.attachRow}>
      {url && isImage ? (
        <Anchor href={url} target="_blank" rel="noopener noreferrer" className={shared.attachThumb} title={label}>
          <Image src={url} alt={label} w={48} h={48} radius="md" fit="cover" />
        </Anchor>
      ) : (
        <Box className={classes.attachIcon}>
          <FileTextIcon size={20} />
        </Box>
      )}
      <Box className={classes.minWidthZero}>
        {url ? (
          <Anchor href={url} target="_blank" rel="noopener noreferrer" className={classes.attachName} title={label} lineClamp={1}>
            {label}
          </Anchor>
        ) : (
          <Badge variant="light" color="gray" radius="sm" size="sm">
            {label}
          </Badge>
        )}
        {attachment?.description?.trim() && (
          <Text className={classes.attachDescription} lineClamp={3}>
            {attachment.description.trim()}
          </Text>
        )}
        {meta && <Text className={shared.metaSub}>{meta}</Text>}
      </Box>
    </Box>
  )
}

export function InvoiceAttachmentsCard({ description, attachments }: { description?: string | null; attachments: any[] }) {
  const { t } = useTranslation("invoice")
  const descriptionText = description?.trim() ?? ""

  if (!descriptionText && attachments.length === 0) return null

  return (
    <Box className={`${detail.card} ${detail.orderAttach}`}>
      <Box className={detail.cardHead}>
        <Title order={2} className={detail.cardTitle}>
          {attachments.length > 0 ? t("detail.attachments") : t("productForm.description")}
        </Title>
        {attachments.length > 0 && (
          <Text component="span" className={detail.cardSub}>
            {attachments.length}
          </Text>
        )}
      </Box>
      <Box className={detail.attachBody}>
        {descriptionText && <Text className={classes.descriptionText}>{descriptionText}</Text>}
        {attachments.length > 0 && (
          <Box className={`${classes.attachList} ${descriptionText ? classes.attachmentsDivided : ""}`}>
            {attachments.map((attachment, index) => (
              <AttachmentRow key={attachment?.id ?? index} attachment={attachment} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
